import AsyncImg from "../../../../../components/extras/AsyncImg";
import useHandleDownloadDocument from "../../../../../hooks/useDownloadDocument";
import { usePrivilege } from "../../../../../components/priviledge/PriviledgeProvider";
import { DocumentsService } from "../../../../../services/document/documentsService";
import { Contract } from "../../../../../type/entities/contract-type";
import { fixNumber } from "../../../../../utils/formatNumber";
import { contractTypeOptions } from "../../../components/ContractDataForm";
import { formatInfoDate, formatInfoValue, InfoCard, InfoField, InfoGroup, InfoStatusField, InfoTextField } from "./components/InfoDisplay";
import { Button } from "rizzui";

interface ContractDataFormInfoProps {
    data: Contract;
}

const ContractDataFormInfo = ({ data }: ContractDataFormInfoProps) => {
    const { userCan } = usePrivilege();
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const documentService = new DocumentsService();

    const contractTypeLabel = contractTypeOptions.find((option) => option.value === data?.contractType)?.label;
    const organisingOrServicesCorporationName = data?.servicesCorporation?.name ?? data?.organisingCorporation?.name;
    const fundaeGroupingLabel = Array.from(
        new Set(
            (data?.fundaeGroupings ?? [])
                .map((item) => item?.fundaeGrouping?.name)
                .filter((name): name is string => Boolean(name))
        )
    ).join(", ");
    const contractPrice = data?.totalAmount != null ? `${fixNumber(Number(data.totalAmount))}€` : "N/A";

    const contractSignatureDocumentId = data?.contractSignatureDocument?.id;
    const directDebitSignatureDocumentId = data?.directDebitMandateSignatureDocument?.id;
    const hasContractSignature = Boolean(data?.isSigned && contractSignatureDocumentId);
    const hasDirectDebitSignature = Boolean(data?.isSignedDirectDebitMandate && directDebitSignatureDocumentId);
    const hasSignatureEvidenceDocument = Boolean(data?.contractSignatureEvidenceDocument?.id);
    const signatureEvidenceName = data?.contractSignatureEvidenceDocument?.originalName || "justificante_firma_contrato";
    const signatureEvidenceExtension = data?.contractSignatureEvidenceDocument?.extension || "pdf";

    return (
        <InfoCard title="Datos Generales del Contrato" subtitle="Información operativa y administrativa asociada al contrato.">
            <InfoGroup title="Información General" description="Datos de gestión y facturación del contrato.">
                <InfoField label="Empresa Organizadora / Servicios" value={formatInfoValue(organisingOrServicesCorporationName)} className="md:col-span-4" />
                <InfoField label="Empresa Impartidora" value={formatInfoValue(data?.providingCorporation?.name)} className="md:col-span-4" />
                <InfoField label="Empresa Plataforma" value={formatInfoValue(data?.platformServiceCorporation?.name)} className="md:col-span-4" />

                <InfoField label="Fecha de Inicio" value={formatInfoDate(data?.productStartDate)} className="md:col-span-4" />
                <InfoField label="Fecha de Fin" value={formatInfoDate(data?.productEndDate)} className="md:col-span-4" />
                <InfoField label="Fecha de Alta" value={formatInfoDate(data?.createdAt)} className="md:col-span-4" />

                <InfoField label="Agrupación Fundae" value={formatInfoValue(fundaeGroupingLabel)} className="md:col-span-4" />
                <InfoField label="Código de Acción Formativa" value={formatInfoValue(data?.product?.code)} className="md:col-span-4" />
                <InfoField label="Código de Grupo" value={formatInfoValue(data?.groupCode?.code ?? data?.groupings)} className="md:col-span-4" />

                <InfoField label="Tipo de Contrato" value={formatInfoValue(contractTypeLabel)} className="md:col-span-4" />
                <InfoField label="Campaña" value={formatInfoValue(data?.campaign?.name)} className="md:col-span-4" />
                <InfoField label="Usuario" value={formatInfoValue(data?.userLogin)} className="md:col-span-4" />

                <InfoField
                    label="Cuota de Seguridad Social"
                    value={data?.socialInsuranceAmount != null ? `${fixNumber(Number(data.socialInsuranceAmount))}€` : "N/A"}
                    className="md:col-span-4"
                />
                <InfoField label="Método de Pago" value={formatInfoValue(data?.paymentMethods?.name)} className="md:col-span-4" />
                <InfoField label="Tipo de Vencimiento" value={formatInfoValue(data?.expirationType?.name)} className="md:col-span-4" />

                <InfoField label="Nº de Plazos" value={formatInfoValue(data?.paymentInstalmentsNumber)} className="md:col-span-4" />
                <InfoField label="Precio" value={contractPrice} className="md:col-span-4" />
                <InfoField label="Fecha de Activación" value={formatInfoDate(data?.activationDate)} className="md:col-span-4" />

                <InfoStatusField label="Estado Incidencia" status={data?.currentIncidenceStatus} className="md:col-span-4" />
                <InfoStatusField label="Estado Comisión" status={data?.currentCommissionStatus} className="md:col-span-4" />
                <InfoStatusField label="Estado Factura" status={data?.currentInvoicingStatus} className="md:col-span-4" />

                <InfoTextField label="Observaciones del Tutor" value={data?.tutorComment} className="md:col-span-6" />
                <InfoTextField label="Observaciones de la Venta" value={data?.saleComment} className="md:col-span-6" />

                <InfoStatusField label="Estado del Contrato" status={data?.currentContractStatus} className="md:col-span-4" />
                {userCan("admin_all_commercial_network_contracts", "contracts") && (
                    <InfoField label="Comercial" value={formatInfoValue(data?.commercial?.name)} className="md:col-span-4" />
                )}
                {userCan("admin_all_commercial_network_contracts", "contracts") && (
                    <InfoField label="Red Comercial" value={formatInfoValue(data?.commercialNetwork?.name)} className="md:col-span-4" />
                )}
                <InfoField label="Fecha de Expiración del Borrador" value={formatInfoDate(data?.draftExpirationDate)} className="md:col-span-4" />
                <InfoField label="Nombre del operador" value={formatInfoValue(data?.operatorName)} className="md:col-span-4" />

                {data?.lastContractKoReason && (
                    <>
                        <InfoField label="Motivo de KO" value={formatInfoValue(data.lastContractKoReason.name)} className="md:col-span-6" />
                        <InfoTextField label="Descripción del motivo de KO" value={data.lastContractKoReason.description ?? ""} className="md:col-span-6" />
                    </>
                )}
            </InfoGroup>

            <InfoGroup title="Firmas del Cliente" description="Firmas capturadas durante el proceso de firma del contrato y la domiciliación.">
                <InfoField
                    label="Firma del Contrato"
                    value={
                        hasContractSignature
                            ? (
                                <AsyncImg
                                    id={contractSignatureDocumentId}
                                    className="h-20 w-full max-w-[280px] rounded-md border border-gray-200 bg-white object-contain p-1"
                                />
                            )
                            : "Sin firma"
                    }
                    className="md:col-span-4"
                />
                <InfoField
                    label="Firma de Domiciliación"
                    value={
                        hasDirectDebitSignature
                            ? (
                                <AsyncImg
                                    id={directDebitSignatureDocumentId}
                                    className="h-20 w-full max-w-[280px] rounded-md border border-gray-200 bg-white object-contain p-1"
                                />
                            )
                            : "Sin firma"
                    }
                    className="md:col-span-8"
                />
                <InfoField label="Fecha de Firma del Contrato" value={formatInfoDate(data?.signDate)} className="md:col-span-4" />
                <InfoField label="Fecha de Firma de la Domiciliación" value={formatInfoDate(data?.signDirectDebitMandateDate)} className="md:col-span-4" />
                <InfoField
                    label="Justificante de Firma"
                    value={hasSignatureEvidenceDocument ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadDocumentNewTab(
                                documentService.downloadDocument,
                                data?.contractSignatureEvidenceDocument?.id || "",
                                signatureEvidenceName,
                                signatureEvidenceExtension
                            )}
                        >
                            Descargar justificante
                        </Button>
                    ) : "Sin justificante"}
                    className="md:col-span-4"
                />
            </InfoGroup>

            <InfoGroup title="Representante Legal" description="Datos del representante legal asociado al contrato.">
                <InfoField label="Nombre y Apellidos" value={formatInfoValue(data?.legalRepresentativeName)} className="md:col-span-4" />
                <InfoField label="NIF/NIE" value={formatInfoValue(data?.legalRepresentativeNif)} className="md:col-span-4" />
                <InfoField label="Correo electrónico" value={formatInfoValue(data?.legalRepresentativeEmail)} className="md:col-span-4" />
                <InfoField label="Cargo" value={formatInfoValue(data?.legalRepresentativePosition)} className="md:col-span-6" />
                <InfoField label="Horario de contacto" value={formatInfoValue(data?.legalRepresentativeSchedule)} className="md:col-span-6" />
            </InfoGroup>
        </InfoCard>
    );
};

export default ContractDataFormInfo;
